// Hold-still detector — cursor/touch idle for 5s → particles momentarily form
// the [HRMNTR] mark for ~1.4s, then dissolve back. Implemented as a DOM
// overlay with letters fading + sliding into place; the shader stays untouched.
//
// Why DOM not shader: rendering glyph shapes in a fragment shader is much more
// code than the visual return. A floating monospace mark with the right blend
// mode and font weight reads as "the page noticed you stopped moving" without
// a separate texture pipeline.

const IDLE_MS = 5000;
const PULSE_MS = 1400;

export interface HoldStillState {
  pulse(): void;       // manually trigger
  noteInput(): void;   // call on every cursor/touch event
  destroy(): void;
}

export function bindHoldStill(root: HTMLElement): HoldStillState {
  const el = document.createElement('div');
  el.className = 'herm-holdstill';
  el.setAttribute('aria-hidden', 'true');
  const word = '[HRMNTR]';
  el.innerHTML = [...word].map((ch, i) => `<span style="--herm-i:${i}">${ch === ' ' ? '&nbsp;' : escapeHtml(ch)}</span>`).join('');
  root.appendChild(el);

  ensureStyles();

  let lastInput = performance.now();
  let pulsing = false;
  let raf = 0;

  const pulse = (): void => {
    if (pulsing) return;
    pulsing = true;
    el.classList.add('herm-holdstill--on');
    setTimeout(() => {
      el.classList.remove('herm-holdstill--on');
      pulsing = false;
      lastInput = performance.now();
    }, PULSE_MS);
  };

  const tick = (): void => {
    if (!pulsing) {
      const now = performance.now();
      if (now - lastInput >= IDLE_MS) pulse();
    }
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);

  return {
    pulse,
    noteInput(): void { lastInput = performance.now(); },
    destroy(): void {
      cancelAnimationFrame(raf);
      el.remove();
    },
  };
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

let styled = false;
function ensureStyles(): void {
  if (styled || typeof document === 'undefined') return;
  const css = `
.herm-holdstill {
  position: fixed;
  left: 50%; top: 50%;
  transform: translate(-50%, -50%);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: clamp(1.4rem, 3vw, 2.4rem);
  letter-spacing: 0.5em;
  color: color-mix(in oklch, var(--herm-pal-b, white) 30%, white 70%);
  mix-blend-mode: difference;
  pointer-events: none;
  opacity: 0;
  z-index: 8;
  white-space: nowrap;
  text-shadow:
    0 0 12px color-mix(in oklch, var(--herm-pal-a, magenta) 50%, transparent),
    0 0 4px color-mix(in oklch, var(--herm-pal-b, cyan) 50%, transparent);
}
.herm-holdstill span {
  display: inline-block;
  transform: translateY(20px) rotate(0deg);
  opacity: 0;
  transition:
    transform 800ms cubic-bezier(0.2, 0.7, 0.1, 1),
    opacity 600ms ease;
  transition-delay: calc(var(--herm-i, 0) * 40ms);
}
.herm-holdstill--on { opacity: 1; }
.herm-holdstill--on span {
  transform: translateY(0) rotate(0deg);
  opacity: 1;
}
@media (prefers-reduced-motion: reduce) {
  .herm-holdstill span { transition-duration: 200ms; }
}
`;
  const s = document.createElement('style');
  s.id = 'herminator-holdstill-styles';
  s.textContent = css;
  document.head.appendChild(s);
  styled = true;
}
